import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CompaniesSection = ({ companies, user, token, preferredLocation }) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Fixed: Always show 6 items per page (3 columns x 2 rows)
  const ITEMS_PER_PAGE = 6;

  // Calculate total pages
  useEffect(() => {
    if (companies.length > 0) {
      const pages = Math.ceil(companies.length / ITEMS_PER_PAGE);
      setTotalPages(pages);
      if (currentPage >= pages) {
        setCurrentPage(0);
      }
    }
  }, [companies.length, currentPage]);

  // Get current page items
  const getCurrentPageItems = () => {
    const start = currentPage * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return companies.slice(start, end);
  };

  const openCompanyDepartments = (company) => {
    navigate("/lkart/dashboard/company-departments", { state: { company } });
  };

  const goToNext = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPage = (index) => {
    setCurrentPage(index);
  };

  const currentItems = getCurrentPageItems();

  // Split items into rows of 3
  const rows = [];
  for (let i = 0; i < currentItems.length; i += 3) {
    rows.push(currentItems.slice(i, i + 3));
  }

  return (
    <>
      {companies.length > 0 && (
        <div className="mb-6 mt-2">
          <h3 className="text-sm font-bold font-montserrat text-[#202020] flex items-center ml-2">
            Companies Near You
          </h3>
          <p className="text-[#333333] mb-4 mt-0.5 text-[10px] font-montserrat ml-2">
            Based on your preferred location{" "}
            <b className="text-indigo-700">{preferredLocation}</b>
          </p>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 relative">
            {/* Navigation Arrows */}
            {totalPages > 1 && (
              <>
                <button
                  onClick={goToPrevious}
                  disabled={currentPage === 0}
                  className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full shadow-lg p-1.5 ml-1 ${
                    currentPage === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-50'
                  } transition-all duration-200`}
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
                <button
                  onClick={goToNext}
                  disabled={currentPage === totalPages - 1}
                  className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full shadow-lg p-1.5 mr-1 ${
                    currentPage === totalPages - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-50'
                  } transition-all duration-200`}
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              </>
            )}

            {/* Companies Grid - 3 columns x 2 rows */}
            <div className="py-2">
              {rows.map((row, rowIndex) => (
                <div key={rowIndex} className="grid grid-cols-3 gap-4 mb-4 last:mb-0">
                  {row.map((company) => (
                    <div
                      key={company._id}
                      onClick={() => openCompanyDepartments(company)}
                      className="cursor-pointer transition-all duration-300 hover:scale-105 flex flex-col items-center"
                    >
                      {/* Company Logo */}
                      <div className="w-14 h-14 rounded-xl border border-[#C9C9C9] bg-gradient-to-br from-white via-[#f9f9ff] to-[#eef1ff] shadow-md hover:shadow-lg flex items-center justify-center p-2">
                        {company.logo ? (
                          <img
                            src={company.logo}
                            alt={company.name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <span className="text-4xl">🏭</span>
                        )}
                      </div>
                      {/* Company Name */}
                      <p className="text-center text-xs text-[#202020] font-medium mt-2 truncate max-w-[80px] font-montserrat">
                        {company.name}
                      </p>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Dynamic Dots */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-1.5 mt-2">
                {Array.from({ length: totalPages }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToPage(index)}
                    className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                      currentPage === index
                        ? 'bg-gray-500 w-4'
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CompaniesSection;